import { Box, Popover, PopoverAnchor, PopoverContent } from '@chakra-ui/react';
import { type ReactNode } from 'react';
import { useFormContext, type FieldValues } from 'react-hook-form';

import { Icon } from '$/components/common/Icon';
import {
  HookFormInput,
  type HookFormInputProps,
} from '$/components/core/Form/HookFormInput';

export interface HookFormDropdownProps<T extends FieldValues>
  extends HookFormInputProps<T> {
  dropdownOpen: boolean;
  dropdownContent: () => ReactNode;
  toggleDropdown: (open: boolean) => void;
  valueLabels: Record<string, string>;
  value?: number | string;
  defaultValue?: number | string;
}

export const HookFormDropdown = <T extends FieldValues>({
  accessor,
  label,
  registerOptions,
  description,
  dropdownOpen,
  toggleDropdown,
  dropdownContent,
  placeholder,
  valueLabels,
  defaultValue,
  ...rest
}: HookFormDropdownProps<T>) => {
  const { watch } = useFormContext<T>();
  const value = watch(accessor);

  return (
    <Popover
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={false}
      closeOnBlur={false}
      gutter={-3}
      isLazy
      isOpen={dropdownOpen}
      onClose={() => toggleDropdown(false)}
      onOpen={() => toggleDropdown(true)}
      placement='bottom-start'
      variant='responsive'
    >
      <PopoverAnchor>
        <Box w='full'>
          <HookFormInput<T>
            type='button'
            accessor={accessor}
            textAlign='start'
            label={label}
            cursor='pointer'
            registerOptions={registerOptions}
            description={description}
            onClick={() => toggleDropdown(!dropdownOpen)}
            value={
              (value && valueLabels[value]) ??
              (defaultValue && valueLabels[defaultValue]) ??
              placeholder
            }
            inputRightElement={
              <Icon icon='chevron_down' mx={4} w='14px' color='lighterText' />
            }
            overwriteRegistration
            {...rest}
          />
        </Box>
      </PopoverAnchor>

      <PopoverContent
        overflowY='auto'
        w='calc(100% - 80px)'
        maxH='300px'
        bg='background'
        borderRadius='0 0 4px 4px'
      >
        {dropdownContent()}
      </PopoverContent>
    </Popover>
  );
};
